import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './ministries.css';
import EditableText from '../../components/editable/EditableText';
import EditableBackground from '../../components/editable/EditableBackground';
import ImageEditModal from '../../components/editable/ImageEditModal';
import { useAuth } from '../../context/AuthContext';
import { useEditMode } from '../../context/EditModeContext';
import { useContent } from '../../context/ContentContext';
import { promptImageUrl, readImageFile } from '../../utils/imageUpload';
import { DEFAULT_MINISTRIES, resolveMinistry } from '../../data/ministries';

const getYouTubeThumbnail = (url) => {
  if (!url) return '';
  const liveMatch = url.match(/youtube\.com\/live\/([^?&/]+)/i);
  const watchMatch = url.match(/[?&]v=([^?&/]+)/i);
  const videoId = liveMatch?.[1] || watchMatch?.[1] || '';
  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : '';
};

const normalizeStreamLink = (link, index) => {
  if (typeof link === 'string') {
    return { url: link, label: `Day ${index + 1}` };
  }
  return {
    url: link?.url || '',
    label: link?.label || `Day ${index + 1}`,
  };
};

const Ministries = () => {
  const { isEditing } = useAuth();
  const { panelOpen } = useEditMode();
  const { getContent, setContentValue } = useContent();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [editingGalleryIndex, setEditingGalleryIndex] = useState(null);
  const sectionRefs = useRef([]);
  const galleryAddRef = useRef(null);

  const selectedMinistry =
    selectedIndex !== null ? resolveMinistry(DEFAULT_MINISTRIES[selectedIndex], selectedIndex, getContent) : null;

  const handleNextImage = useCallback(() => {
    if (selectedMinistry) {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % selectedMinistry.images.length);
    }
  }, [selectedMinistry]);

  const handlePrevImage = useCallback(() => {
    if (selectedMinistry) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === 0 ? selectedMinistry.images.length - 1 : prevIndex - 1
      );
    }
  }, [selectedMinistry]);

  const saveGalleryImages = (images) => {
    if (selectedIndex === null) return;
    setContentValue(`ministries.${selectedIndex}.images`, images);
  };

  const applyGalleryFile = async (file, handler) => {
    try {
      const dataUrl = await readImageFile(file);
      handler(dataUrl);
    } catch (error) {
      window.alert(error.message);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (selectedMinistry) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev || '';
      };
    }
  }, [selectedMinistry]);

  useEffect(() => {
    const currentRefs = sectionRefs.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          } else {
            entry.target.classList.remove('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    currentRefs.forEach((ref) => ref && observer.observe(ref));

    return () => {
      currentRefs.forEach((ref) => ref && observer.unobserve(ref));
    };
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!selectedMinistry) return;

      switch (e.key) {
        case 'Escape':
          if (isFullScreen) {
            setIsFullScreen(false);
          } else {
            setSelectedIndex(null);
          }
          break;
        case 'ArrowRight':
          if (isFullScreen) {
            handleNextImage();
          }
          break;
        case 'ArrowLeft':
          if (isFullScreen) {
            handlePrevImage();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedMinistry, isFullScreen, handleNextImage, handlePrevImage]);

  const handleCardClick = (index, event) => {
    if (event?.target?.closest?.('.ministry-gallery-edit-btn, .editable-text, [contenteditable="true"], .editable-image-toolbar, .editable-image-btn')) {
      return;
    }
    setSelectedIndex(index);
    setCurrentImageIndex(0);
    setIsFullScreen(false);
    setEditingGalleryIndex(null);
  };

  const handleCloseDialog = () => {
    setSelectedIndex(null);
    setIsFullScreen(false);
    setEditingGalleryIndex(null);
  };

  const handleImageClick = (index) => {
    if (isEditing) return;
    setCurrentImageIndex(index);
    setIsFullScreen(true);
  };

  const handleCloseFullScreen = () => {
    setIsFullScreen(false);
  };

  return (
    <div className="ministries">
      <div className="ministries-header">
        <EditableText contentKey="ministries.hero.title" tag="h1" className="ministries-title">
          OUR MINISTRIES
        </EditableText>
      </div>

      <div className="ministries-description-container">
        <EditableText contentKey="ministries.description" tag="p" className="ministries-description" multiline>
          Our church ministries include All-night Prayer, India Mission, Street Mission, Bootcamp, Community Outreach, Music Night, and more.
        </EditableText>
        <EditableText contentKey="ministries.description_sub" tag="p" className="ministries-description-sub" multiline>
          Each ministry is built to serve people and share God&apos;s love. Choose a ministry below to learn more.
        </EditableText>
      </div>

      <div className="ministries-cards">
        {DEFAULT_MINISTRIES.map((ministry, index) => (
          <div
            key={index}
            ref={(el) => (sectionRefs.current[index] = el)}
            className="ministry-card hidden"
            onClick={(e) => handleCardClick(index, e)}
          >
            <EditableBackground
              contentKey={`ministries.${index}.cover`}
              src={ministry.image}
              className="ministry-card-bg"
            />
            <div className="ministry-card-content">
              <EditableText contentKey={`ministries.${index}.name`} tag="h2" className="ministry-name">
                {ministry.name}
              </EditableText>
            </div>
          </div>
        ))}
      </div>

      {selectedMinistry && createPortal(
        <div className="ministries ministries-modal-root">
      {selectedIndex !== null && !isFullScreen && (
        <div
          className={`dialog-overlay${panelOpen ? ' dialog-overlay--editor-open' : ''}${isEditing ? ' dialog-overlay--editing' : ''}`}
          onClick={handleCloseDialog}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ministry-dialog-title"
        >
          <div className="dialog-box dialog-box-ministry" onClick={(e) => e.stopPropagation()}>
            <button className="dialog-close" onClick={handleCloseDialog} aria-label="Close">
              &times;
            </button>
            <EditableBackground
              contentKey={`ministries.${selectedIndex}.cover`}
              src={DEFAULT_MINISTRIES[selectedIndex].image}
              className="dialog-hero"
            >
              <div className="dialog-hero-overlay" />
              <div className="dialog-hero-content">
                <p className="dialog-page-label">Ministry</p>
                <EditableText
                  contentKey={`ministries.${selectedIndex}.name`}
                  tag="h2"
                  id="ministry-dialog-title"
                  className="dialog-title"
                >
                  {DEFAULT_MINISTRIES[selectedIndex].name}
                </EditableText>
              </div>
            </EditableBackground>
            <div className="dialog-body">
              <EditableText
                contentKey={`ministries.${selectedIndex}.description`}
                tag="p"
                className="dialog-description"
                multiline
              >
                {DEFAULT_MINISTRIES[selectedIndex].description}
              </EditableText>
              {selectedMinistry.update?.body && (
                <section className="dialog-update" aria-label="Ministry update">
                  <div className="dialog-update-copy">
                    {selectedMinistry.update.title && (
                      <EditableText
                        contentKey={`ministries.${selectedIndex}.update.title`}
                        tag="h3"
                        className="dialog-update-title"
                      >
                        {DEFAULT_MINISTRIES[selectedIndex].update?.title || selectedMinistry.update.title}
                      </EditableText>
                    )}
                    <EditableText
                      contentKey={`ministries.${selectedIndex}.update.body`}
                      tag="p"
                      className="dialog-update-body"
                      multiline
                    >
                      {DEFAULT_MINISTRIES[selectedIndex].update?.body || selectedMinistry.update.body}
                    </EditableText>
                  </div>
                  {selectedMinistry.update.images?.length > 0 && (
                    <div className="dialog-update-photos">
                      {selectedMinistry.update.images.map((img, idx) => {
                        const galleryIndex = selectedMinistry.images.indexOf(img);
                        const openIndex = galleryIndex >= 0 ? galleryIndex : idx;
                        return (
                          <button
                            key={`update-${idx}`}
                            type="button"
                            className="dialog-update-photo"
                            onClick={() => !isEditing && handleImageClick(openIndex)}
                            disabled={isEditing}
                            aria-label={`Open gallery image ${openIndex + 1}`}
                          >
                            <img
                              src={img}
                              alt={`${selectedMinistry.name} update ${idx + 1}`}
                              loading="lazy"
                            />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}
              {selectedMinistry.streams && (
                <div className="dialog-streams-wrap">
                  <p className="dialog-gallery-label">Messages</p>
                  <div className="dialog-stream-groups">
                    {selectedMinistry.streams.map((streamGroup) => (
                      <div key={streamGroup.speaker} className="dialog-stream-group">
                        <div className="dialog-stream-header">
                          <p className="dialog-stream-speaker">{streamGroup.speaker}</p>
                          <span className="dialog-stream-count">
                            {streamGroup.links.length} message{streamGroup.links.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div className="dialog-stream-links">
                          {streamGroup.links.map((rawLink, idx) => {
                            const link = normalizeStreamLink(rawLink, idx);
                            return (
                              <a
                                key={`${streamGroup.speaker}-${idx}`}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="dialog-stream-link"
                              >
                                <span className="dialog-stream-thumb-wrap">
                                  <img
                                    src={getYouTubeThumbnail(link.url)}
                                    alt=""
                                    className="dialog-stream-thumb"
                                    loading="lazy"
                                  />
                                  <span className="dialog-stream-play" aria-hidden="true">▶</span>
                                </span>
                                <span className="dialog-stream-copy">
                                  <span className="dialog-stream-day">{link.label}</span>
                                  <span className="dialog-stream-watch">Watch message</span>
                                </span>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="dialog-gallery-wrap" data-content-key={`ministries.${selectedIndex}.images`}>
                <p className="dialog-gallery-label">Gallery</p>
                <div className="dialog-images">
                  {selectedMinistry.images.map((img, idx) => (
                    <div
                      key={idx}
                      className={`dialog-image-item${isEditing ? ' dialog-image-item--editable' : ''}`}
                    >
                      {isEditing ? (
                        <>
                          <img
                            src={img}
                            alt={`${selectedMinistry.name} ${idx + 1}`}
                            className="dialog-image"
                            loading="lazy"
                          />
                          <button
                            type="button"
                            className="ministry-gallery-edit-btn"
                            aria-label={`Edit gallery image ${idx + 1}`}
                            onClick={() => setEditingGalleryIndex(idx)}
                          >
                            Edit
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="dialog-image-wrap"
                          onClick={() => handleImageClick(idx)}
                        >
                          <img
                            src={img}
                            alt={`${selectedMinistry.name} ${idx + 1}`}
                            className="dialog-image"
                            loading="lazy"
                          />
                        </button>
                      )}
                    </div>
                  ))}
                  {isEditing && (
                    <div className="dialog-image-item dialog-image-item--add">
                      <button
                        type="button"
                        className="dialog-gallery-add-tile"
                        onClick={() => galleryAddRef.current?.click()}
                      >
                        <span className="dialog-gallery-add-icon">+</span>
                        <span className="dialog-gallery-add-label">Upload image</span>
                      </button>
                      <button
                        type="button"
                        className="dialog-gallery-add-link"
                        onClick={() => {
                          const url = promptImageUrl();
                          if (url) saveGalleryImages([...selectedMinistry.images, url]);
                        }}
                      >
                        Add from URL
                      </button>
                    </div>
                  )}
                </div>
                <input
                  ref={galleryAddRef}
                  type="file"
                  accept="image/*"
                  className="editable-image-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    applyGalleryFile(file, (dataUrl) => {
                      saveGalleryImages([...selectedMinistry.images, dataUrl]);
                    });
                    e.target.value = '';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {editingGalleryIndex !== null && (
        <ImageEditModal
          title={`Edit gallery image ${editingGalleryIndex + 1}`}
          image={{ src: selectedMinistry.images[editingGalleryIndex] }}
          onClose={() => setEditingGalleryIndex(null)}
          onSaveUrl={(url) => {
            const nextImages = [...selectedMinistry.images];
            nextImages[editingGalleryIndex] = url;
            saveGalleryImages(nextImages);
            setEditingGalleryIndex(null);
          }}
          onSaveImage={(dataUrl) => {
            const nextImages = [...selectedMinistry.images];
            nextImages[editingGalleryIndex] = dataUrl;
            saveGalleryImages(nextImages);
          }}
          onRemove={() => {
            saveGalleryImages(selectedMinistry.images.filter((_, i) => i !== editingGalleryIndex));
            setEditingGalleryIndex(null);
          }}
        />
      )}

      {isFullScreen && (
        <div className="dialog-fullscreen-container" onClick={handleCloseFullScreen}>
          <div className="dialog-lightbox" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-lightbox-header">
              <div className="dialog-lightbox-meta">
                <p className="dialog-lightbox-label">{selectedMinistry.name}</p>
                <p className="dialog-lightbox-count">
                  {currentImageIndex + 1} / {selectedMinistry.images.length}
                </p>
              </div>
              <button className="dialog-close-fullscreen" onClick={handleCloseFullScreen} aria-label="Close">
                &times;
              </button>
            </div>
            <div className="dialog-lightbox-main">
              <button className="dialog-arrow left-arrow" onClick={handlePrevImage} aria-label="Previous image">
                &#10094;
              </button>
              <div className="dialog-fullscreen-image-wrap">
                <img
                  src={selectedMinistry.images[currentImageIndex]}
                  alt={`${selectedMinistry.name} ${currentImageIndex + 1}`}
                  className="dialog-fullscreen-image"
                />
              </div>
              <button className="dialog-arrow right-arrow" onClick={handleNextImage} aria-label="Next image">
                &#10095;
              </button>
            </div>
            <div className="dialog-thumbnails" aria-label="Image thumbnails">
              {selectedMinistry.images.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`dialog-thumb ${idx === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(idx)}
                  aria-label={`Open image ${idx + 1}`}
                >
                  <img src={img} alt={`${selectedMinistry.name} thumbnail ${idx + 1}`} loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Ministries;
